import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { SharedProposalResponse } from '@shared/api';
import { BrandMark } from '../components/BrandMark';
import { getSharedProposal, setShareToken } from '../api/client';
import { withShareToken } from '../lib/mediaUrls';
import { PageFrame } from '../pages/Page';
import { PageArchetype } from '../pages/registry';
import { READ_ONLY_ACTIONS } from '../pages/types';
import { themeStyle } from '../lib/theme';
import { visibleSections } from '../lib/plan';
import { buildContentLibraryStore, ContentLibraryContext } from '../state/ContentLibraryContext';

/**
 * `/share/:token` - the read-only client link. Same page components as the
 * workspace, one wide page at a time with next/prev; no sign-in, no app
 * chrome beyond a minimal brand bar, no editing. The server has already
 * stripped every `internal`-visibility value before this payload existed.
 */
export function SharedView({ token }: { token: string }): ReactElement {
  const [data, setData] = useState<SharedProposalResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Every fetch this view makes (via request() in api/client.ts) carries
    // this token — the credential GET /api/proposals/:id and its media now
    // require in place of a session (server/index.ts's /api gate). Media
    // <img> URLs can't carry a header, so those get it via withShareToken
    // below instead. Cleared on unmount so it never rides along on a later,
    // unrelated authenticated request.
    setShareToken(token);
    return () => setShareToken(null);
  }, [token]);

  useEffect(() => {
    let live = true;
    void getSharedProposal(token)
      .then((res) => {
        if (live) setData(res);
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [token]);

  // Media URLs inside the document (e.g. hero images, logos) are same-origin
  // paths an <img> tag fetches directly — it can't carry the X-Share-Token
  // header above, so they get the token as a ?share= query param instead
  // (accepted only on the media route).
  const proposal = useMemo(
    () => (data?.proposal ? withShareToken(data.proposal, token) : null),
    [data, token],
  );
  const sections = useMemo(() => (proposal ? visibleSections(proposal) : []), [proposal]);
  const total = sections.length;

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (total === 0) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setIndex((value) => (value + 1 >= total ? 0 : value + 1));
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setIndex((value) => (value - 1 < 0 ? total - 1 : value - 1));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [total]);

  if (error) return <p className="loading">{error}</p>;
  if (!data || !proposal) return <p className="loading">Loading proposal…</p>;
  if (!proposal.document) return <p className="loading">This proposal has no document yet.</p>;

  const theme = proposal.document.theme;
  const section = sections[index] ?? sections[0] ?? null;

  return (
    <ContentLibraryContext.Provider value={buildContentLibraryStore(data.library, data.products)}>
      <div className="share-shell" style={themeStyle(theme)}>
        <header className="share-bar">
          <BrandMark theme={theme} fallback={proposal.advertiser.name ?? 'Media Proposal'} />
          <div className="share-bar-right">
            {total > 0 ? (
              <span className="share-indicator">
                {index + 1} / {total}
              </span>
            ) : null}
            <button type="button" className="btn btn-quiet" onClick={() => window.print()}>
              Print
            </button>
          </div>
        </header>
        <div className="share-stage">
          {section ? (
            <>
              <button
                type="button"
                className="focus-arrow focus-arrow-left"
                aria-label="Previous page"
                disabled={total < 2}
                onClick={() => setIndex((value) => (value - 1 < 0 ? total - 1 : value - 1))}
              >
                ‹
              </button>
              <PageFrame className="share-frame">
                <PageArchetype proposal={proposal} section={section} actions={READ_ONLY_ACTIONS} readOnly />
              </PageFrame>
              <button
                type="button"
                className="focus-arrow focus-arrow-right"
                aria-label="Next page"
                disabled={total < 2}
                onClick={() => setIndex((value) => (value + 1 >= total ? 0 : value + 1))}
              >
                ›
              </button>
            </>
          ) : (
            <p className="loading">This proposal has no visible pages yet.</p>
          )}
        </div>
        {/* Print renders every visible page sequentially, independent of the pager above. */}
        <div className="print-pages share-print-pages" style={themeStyle(theme)}>
          {sections.map((printSection) => (
            <PageFrame key={printSection.id} className="print-page">
              <PageArchetype proposal={proposal} section={printSection} actions={READ_ONLY_ACTIONS} readOnly />
            </PageFrame>
          ))}
        </div>
      </div>
    </ContentLibraryContext.Provider>
  );
}
